import { lazy } from "react";
import { Link } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Users } from "src/page/users/usersSlice";
import { EMPTY_STRING, roleMapping } from "src/components/constant/constant";
import { bgColorbyName, roleColors } from "src/variable/randomColor";
import { formatFullName } from "src/utils";
import HighLighter from "src/components/UI/highlighter/HighLighter";
import { ShortIdChip } from "src/components/UI/shortIdChip";
import { DateStack, EmptyCell, cellClasses } from "src/components/UI/transactionCells";
import StatusPill from "src/components/userComponent/profileHero/StatusPill";
import styles from "./internalUser.module.scss";

const EditUserModal = lazy(() => import("src/components/InternalUserList/editUser/EditUserModal"));

const ROLE_FALLBACK_TONE = "#475569";

const internalUserColumns = (searchText: string = EMPTY_STRING, canViewDetail: boolean = true) => {
  const memberCell = (_value: unknown, record: Users) => {
    const name = formatFullName(record?.firstName, record?.lastName);
    const display = name || record?.email;

    if (!display) return <EmptyCell />;

    const memberContent = (
      <>
        <Avatar aria-label="Member" className={cellClasses.memberAvatar} sx={{ bgcolor: bgColorbyName(display) }}>
          {display.charAt(0).toLocaleUpperCase()}
        </Avatar>
        <div className={cellClasses.tableMemberDetail}>
          <div className={cellClasses.tableMemberName}>
            <HighLighter searchText={searchText} text={name || record?.email || EMPTY_STRING} />
          </div>
          {name && record?.email && (
            <div className={cellClasses.tableMemberEmail}>
              <HighLighter searchText={searchText} text={record.email} />
            </div>
          )}
        </div>
      </>
    );

    if (canViewDetail) {
      return (
        <Link to={`/internal-users/${record?.userId}`} className={cellClasses.tableMember}>
          {memberContent}
        </Link>
      );
    }

    return <div className={cellClasses.tableMember}>{memberContent}</div>;
  };

  const viewColumn = {
    title: EMPTY_STRING,
    dataIndex: EMPTY_STRING,
    key: "edit",
    render: (_text: string, record: Users) => (
      <Tooltip title="View user" placement="top" arrow>
        <IconButton
          component={Link}
          to={`/internal-users/${record?.userId}`}
          size="small"
          className={styles.viewBtn}
          aria-label={`View user ${formatFullName(record?.firstName, record?.lastName) || record?.email}`}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    width: 64
  };

  return [
    {
      title: "ID",
      dataIndex: "userId",
      key: "userId",
      render: (userId: string) => (
        <ShortIdChip id={userId ?? EMPTY_STRING} copiedLabel="User ID Copied!" fallback="---" />
      )
    },
    {
      title: "Display ID",
      dataIndex: "id",
      key: "id",
      render: (id: string | number) => (
        <ShortIdChip id={String(id ?? EMPTY_STRING)} copiedLabel="Display ID Copied!" fallback="---" />
      )
    },
    {
      title: "Date / Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (createdAt: string) => <DateStack value={createdAt} />
    },
    {
      title: "Team Member",
      dataIndex: "fullName",
      key: "fullName",
      render: memberCell
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role: string) => {
        const roleKey = role || "NOROLE";
        const label = roleMapping[roleKey];

        if (!label) return <EmptyCell />;

        const tone = roleColors[roleKey] ?? ROLE_FALLBACK_TONE;

        return (
          <span
            className={cellClasses.statusPill}
            style={{
              backgroundColor: `${tone}14`,
              borderColor: `${tone}40`,
              color: tone
            }}
          >
            {label}
          </span>
        );
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <StatusPill variant={status ? "active" : "inactive"} label={status ? "Active" : "Inactive"} />
      )
    },
    ...(canViewDetail ? [viewColumn] : []),
    {
      title: EMPTY_STRING,
      dataIndex: EMPTY_STRING,
      key: "editUser",
      render: (_text: string, record: Users) => <EditUserModal user={record} />,
      width: 64
    }
  ];
};

export default internalUserColumns;
